import { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { VideoProcessor } from '@/lib/video-processor';

interface BaseVideoProcessorSelectorProps {
  processors: Pick<VideoProcessor, 'id' | 'name'>[];
  selectedProcessorIds: string[];
  onSelect: (processorIds: string[]) => void;
  placeholder?: string;
  className?: string;
  allowDeselectAll?: boolean;
}

export function MultiSelectVideoProcessorSelector({
  processors,
  selectedProcessorIds = [],
  onSelect,
  placeholder = 'Select cameras...',
  className,
  allowDeselectAll = true,
}: BaseVideoProcessorSelectorProps) {
  const [open, setOpen] = useState(false);

  const handleSelect = (processorId: string) => {
    const newSelection = selectedProcessorIds.includes(processorId)
      ? selectedProcessorIds.filter((id) => id !== processorId)
      : [...selectedProcessorIds, processorId];

    // Prevent deselecting all if not allowed and this would result in empty selection
    if (!allowDeselectAll && newSelection.length === 0) {
      return;
    }

    onSelect(newSelection);
  };

  const selectedProcessors = processors.filter((p) => selectedProcessorIds.includes(p.id));
  const displayText =
    selectedProcessors.length > 0 ? selectedProcessors.map((p) => p.name).join(', ') : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'w-[200px] justify-between border-gray-600 bg-transparent text-white hover:bg-gray-800 hover:text-white',
            className
          )}
        >
          <span className="truncate">{displayText}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0">
        <Command className="bg-gray-900 text-white">
          <CommandInput placeholder="Search cameras..." />
          <CommandEmpty>No cameras found.</CommandEmpty>
          <CommandList>
            <CommandGroup className="max-h-[200px] overflow-y-auto">
              {processors.map((processor) => (
                <CommandItem
                  key={processor.id}
                  keywords={[processor.name]}
                  value={processor.id}
                  onSelect={() => handleSelect(processor.id)}
                  className="cursor-pointer font-bold"
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      selectedProcessorIds.includes(processor.id) ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  {processor.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

interface SingleSelectVideoProcessorSelectorProps {
  processors: VideoProcessor[];
  selectedProcessorId?: string;
  onSelect: (processorId: string) => void;
  placeholder?: string;
  className?: string;
}

export function VideoProcessorSelector({
  processors,
  selectedProcessorId,
  onSelect,
  placeholder = 'Select camera...',
  className,
}: SingleSelectVideoProcessorSelectorProps) {
  const [open, setOpen] = useState(false);

  const handleSelect = (processorId: string) => {
    if (processorId !== selectedProcessorId) {
      onSelect(processorId);
    }
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'w-[200px] justify-between border-gray-600 bg-transparent text-white hover:bg-gray-800 hover:text-white',
            className
          )}
        >
          <span className="truncate">
            {selectedProcessorId
              ? processors.find((p) => p.id === selectedProcessorId)?.name || placeholder
              : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0">
        <Command className="bg-gray-900 text-white">
          <CommandInput placeholder="Search cameras..." />
          <CommandEmpty>No cameras found.</CommandEmpty>
          <CommandList>
            <CommandGroup className="max-h-[200px] overflow-y-auto">
              {processors.map((processor) => (
                <CommandItem
                  key={processor.id}
                  keywords={[processor.name]}
                  value={processor.id}
                  onSelect={() => handleSelect(processor.id)}
                  className="cursor-pointer font-bold"
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      selectedProcessorId === processor.id ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  {processor.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
